/**
 * Color Extraction Utility
 *
 * Extracts dominant colors from product images using the same algorithm
 * as V2's variant-representation.component.tsx getMaterialColors().
 *
 * Uses the `extract-colors` library (v4.0.8) with tuned parameters
 * for product material/texture images.
 */

import { extractColors } from 'extract-colors';

/**
 * Calculate Euclidean distance between two hex colors in RGB space.
 * Used to ensure selected colors are visually distinct.
 */
export const colorDistance = (color1: string, color2: string): number => {
  const hexToRgb = (hex: string) => {
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    return result
      ? {
          r: parseInt(result[1], 16),
          g: parseInt(result[2], 16),
          b: parseInt(result[3], 16),
        }
      : null;
  };

  const rgb1 = hexToRgb(color1);
  const rgb2 = hexToRgb(color2);

  if (!rgb1 || !rgb2) return 0;

  return Math.sqrt(
    Math.pow(rgb1.r - rgb2.r, 2) +
      Math.pow(rgb1.g - rgb2.g, 2) +
      Math.pow(rgb1.b - rgb2.b, 2)
  );
};

/**
 * Read a File/Blob as a data URL string.
 */
function readAsDataUrl(file: File | Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(file);
  });
}

/**
 * Extract up to 2 visually distinct dominant colors from an image file.
 *
 * Algorithm (identical to V2):
 * 1. Read file as data URL
 * 2. Extract colors with tuned parameters (distance, saturation, lightness, pixel sampling)
 * 3. Sort by area (dominance) descending
 * 4. Pick the most dominant color
 * 5. Find a second color that's at least 50 RGB units different
 * 6. Return up to 2 hex codes
 *
 * @param imageFile - The image File or Blob to analyze
 * @returns Array of up to 2 hex color strings, or empty array on error
 */
export async function extractDominantColors(imageFile: File | Blob): Promise<string[]> {
  try {
    const imageUrl = await readAsDataUrl(imageFile);

    // Extract colors with V2-proven parameters
    const colorsArray = await extractColors(imageUrl, {
      distance: 0.15,
      saturationDistance: 0.4,
      lightnessDistance: 0.08,
      pixels: 64000,
    });

    if (colorsArray.length === 0) {
      return [];
    }

    // Sort colors by area (dominance) in descending order
    const sortedColors = colorsArray.sort((a, b) => (b.area || 0) - (a.area || 0));

    // Always take the most dominant color first
    const dominantColors = [sortedColors[0]];

    // Find a second color that's sufficiently different from the first
    if (sortedColors.length > 1) {
      const dominantHex = sortedColors[0].hex;

      // Look for a color that's at least 50 units different in RGB space
      for (let i = 1; i < sortedColors.length; i++) {
        const candidateHex = sortedColors[i].hex;
        if (colorDistance(dominantHex, candidateHex) > 50) {
          dominantColors.push(sortedColors[i]);
          break;
        }
      }
    }

    // Limit to maximum 2 colors
    return dominantColors.slice(0, 2).map((color) => color.hex);
  } catch (error) {
    console.error('[colorExtraction] Failed to extract colors:', error);
    return [];
  }
}
